import React from 'react';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import getApiVersionUrl from 'ui/snippets/footer/utils/getApiVersionUrl';

import {
  PRODUCT_LINKS,
  PROJECT_LINKS,
  RESOURCE_LINKS,
  DEVNET_LINKS,
  LEGAL_LINKS,
  DISCORD_URL,
  LINKEDIN_URL,
  INSTAGRAM_URL,
  X_URL,
  CONTACT_EMAIL,
  TAGLINE,
  LOCATION,
  RIGHTS,
  CREDENTIALS_EYEBROW,
  TRADEMARK_NOTE,
  GITHUB_URL,
  CERTIFICATIONS,
  CERTIFICATIONS_EYEBROW,
} from './basisLinks';
import BasisLogo from './BasisLogo';
import DiscordMark from './DiscordMark';

// [Basis chrome] El footer del explorador es el de la landing.
//
// Port de frontend_landing/src/components/Footer.astro: misma retícula de 12
// columnas, mismas cinco secciones, misma tira de respaldo y misma franja
// inferior. Estilos en nextjs/basis-chrome.css.
//
// QUE CAMBIA RESPECTO A LA LANDING, y por que:
//
//  - Los enlaces propios del explorador (API REST, GraphQL) entran en «Para
//    desarrolladores», despues de los de la landing y antes de «Código en
//    GitHub», en vez de abrir una sexta columna. La retícula de la landing es
//    4+2+2+2+2 = 12; una columna mas la desborda. Son los mismos enlaces que
//    hasta ahora publicaba brand/assets/footer-links.json, que este componente
//    deja de leer: NEXT_PUBLIC_FOOTER_LINKS solo lo consumia el footer de
//    Blockscout. El fichero se conserva por si se vuelve atras.
//
//  - La franja inferior lleva ademas la atribucion a Blockscout y las versiones
//    de backend y frontend. Lo primero es un compromiso ya escrito en
//    envs/frontend.env («the footer 'powered by Blockscout' attribution stays,
//    which is where credit belongs»); lo segundo es informacion de operacion que
//    se pierde si el footer propio no la trae, y es el unico sitio del producto
//    donde se puede ver que version corre.
//
//  - No se portan dos piezas del footer de Blockscout porque en este despliegue
//    no renderizan nada: IntTxsIndexingStatus (oculto por
//    NEXT_PUBLIC_HIDE_INDEXING_ALERT_INT_TXS=true) y NetworkAddToWallet (exige
//    NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID, sin definir). Tampoco el aviso de
//    reCAPTCHA, que solo aparece con NEXT_PUBLIC_RE_CAPTCHA_V2_APP_SITE_KEY. Si
//    alguna de las tres se enciende, hay que volver aqui.
//
//  - El boton «Preferencias de cookies» SI viaja: reabre el panel de
//    BasisCookieConsent, que escucha `data-consent-reopen` por delegacion en
//    document (el pie es otro arbol de React). Ver la franja inferior.
//
//  - Colores de la marca nueva (2026-10): el pie es Gris oscuro #3c3c3c --el
//    mismo en los dos temas--, los encabezados de columna son h2 blancos en
//    seminegrita y minuscula normal (fuera el rotulo en mono, mayusculas y
//    espaciado ancho), y el texto de apoyo va en blanco translucido (70 / 60 /
//    25 %) y no en los zinc que se leian sobre el azul marino. Hasta 2026-10 el
//    pie era #0B2540.

const YEAR_OF_RELEASE = 2026;

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <div className="bs-footer__col">
    <h2 className="bs-footer__heading">{ title }</h2>
    <ul className="bs-footer__list">{ children }</ul>
  </div>
);

const Item = ({ href, children, external }: { href: string; children: React.ReactNode; external?: boolean }) => (
  <li>
    <a href={ href } { ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }>
      { children }
    </a>
  </li>
);

const BasisFooter = () => {
  const { data: backendVersionData } = useApiQuery('general:config_backend_version', {
    queryOptions: {
      staleTime: Infinity,
      enabled: !config.features.opSuperchain.isEnabled,
    },
  });

  const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  const frontendVersion = config.UI.footer.frontendVersion;

  // El anio se calcula en cliente. En servidor se pinta el de la version para
  // no producir un HTML distinto al de la hidratacion.
  const [ year, setYear ] = React.useState(YEAR_OF_RELEASE);
  React.useEffect(() => setYear(new Date().getFullYear()), []);

  return (
    <footer className="bs-chrome bs-footer">
      <div className="bs-footer__main">
        <div className="bs-footer__grid">

          { /* Marca */ }
          <div className="bs-footer__col--brand">
            { /* Wordmark Blanco #fafefe: este pie es Gris oscuro en los dos temas. */ }
            <div className="bs-footer__brandmark">
              <BasisLogo variant="white" lazy/>
            </div>
            <p className="bs-footer__tagline">{ TAGLINE }</p>

            <div className="bs-footer__social">
              <a href={ LINKEDIN_URL } target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href={ INSTAGRAM_URL } target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a href={ X_URL } target="_blank" rel="noopener noreferrer" aria-label="X / Twitter">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href={ DISCORD_URL } target="_blank" rel="noopener noreferrer" aria-label="Únete a la comunidad de Basis Network en Discord">
                <DiscordMark size={ 16 }/>
              </a>
              { /* GitHub va el ultimo de los cinco: es el unico que no es una red
                   social sino el codigo, y quien lo busca lo busca a proposito. */ }
              <a href={ GITHUB_URL } target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                </svg>
              </a>
            </div>

            { /* Contacto vive bajo la marca, no en su propia columna. Se mudo
                 aqui cuando entro «Para desarrolladores»: cinco columnas de
                 enlaces mas la marca no caben en la reticula de 12, y el correo
                 en una columna de dos unidades se partia a mitad de palabra. */ }
            <div className="bs-footer__contact">
              <h2 className="bs-footer__heading">Contacto</h2>
              <p className="bs-footer__org">Basis Network</p>
              <p className="bs-footer__place">{ LOCATION }</p>
              <a href={ `mailto:${ CONTACT_EMAIL }` } className="bs-footer__mail">{ CONTACT_EMAIL }</a>
            </div>
          </div>

          <Section title="Cimientos">
            { PROJECT_LINKS.map(link => <Item key={ link.text } href={ link.url }>{ link.text }</Item>) }
          </Section>

          <Section title="Producto">
            { PRODUCT_LINKS.map(link => <Item key={ link.text } href={ link.url }>{ link.footerText ?? link.text }</Item>) }
          </Section>

          <Section title="Recursos">
            <li>
              <a href={ DISCORD_URL } target="_blank" rel="noopener noreferrer">
                <DiscordMark size={ 14 }/>
                Discord
              </a>
            </li>
            { RESOURCE_LINKS.map(link => <Item key={ link.text } href={ link.url }>{ link.text }</Item>) }
          </Section>

          <Section title="Para desarrolladores">
            { DEVNET_LINKS.map(link => <Item key={ link.text } href={ link.url }>{ link.text }</Item>) }
          </Section>

        </div>
      </div>

      { /* LA TIRA DE ABAJO: dos clases de tercero, una a cada lado.

           IZQUIERDA, respaldo institucional -- quien nos ha reconocido. Sus
           logos son las variantes en negativo del footer de la landing, y la
           nota de marcas viaja palabra por palabra porque es el descargo que
           las acompana.

           DERECHA, certificaciones -- que hemos cumplido y cualquiera puede
           comprobar. Van con SUS colores, sin blanquear ni redibujar, porque
           son el sello del emisor, y todas enlazan a la pagina donde se
           verifica el estado. Ver la nota de CERTIFICATIONS en basisLinks.ts,
           que explica por que las dos de OpenChain comparten `href` y por que
           sus PNG no son los que uno descarga del emisor. */ }
      <div className="bs-footer__band">
        <div className="bs-footer__band-inner">
          <div className="bs-footer__band-grid">

            <div>
              <h2 className="bs-footer__heading bs-footer__heading--band">{ CREDENTIALS_EYEBROW }</h2>

              <div className="bs-footer__creds">
                <img
                  src="/basis-chrome/avalanche-white.svg"
                  alt="Avalanche"
                  width={ 1835 } height={ 271 }
                  style={{ height: '19px', width: 'auto' }}
                  loading="lazy" decoding="async"
                />
                <div>
                  <img
                    src="/basis-chrome/gen-n-white.svg"
                    alt="Gen N"
                    width={ 332 } height={ 114 }
                    style={{ height: '24px', width: 'auto' }}
                    loading="lazy" decoding="async"
                  />
                  <img
                    src="/basis-chrome/ruta-n-white.svg"
                    alt="Ruta N Medellín"
                    width={ 136 } height={ 80 }
                    style={{ height: '30px', width: 'auto', marginTop: '0.625rem' }}
                    loading="lazy" decoding="async"
                  />
                </div>
                <div>
                  <img
                    src="/basis-chrome/google-for-startups-white.svg"
                    alt="Google for Startups Cloud Program"
                    width={ 824 } height={ 100 }
                    style={{ width: '128px', height: 'auto' }}
                    loading="lazy" decoding="async"
                  />
                  <img
                    src="/basis-chrome/gfs-cloud-program-white.svg"
                    alt="" aria-hidden="true"
                    width={ 204 } height={ 50 }
                    style={{ width: '115px', height: 'auto', marginTop: '0.25rem' }}
                    loading="lazy" decoding="async"
                  />
                </div>
              </div>

              <p className="bs-footer__note">{ TRADEMARK_NOTE }</p>
            </div>

            <div>
              <h2 className="bs-footer__heading bs-footer__heading--band bs-footer__heading--right">{ CERTIFICATIONS_EYEBROW }</h2>

              <div className="bs-footer__certs">
                { CERTIFICATIONS.map(cert => (
                  <a
                    key={ cert.alt }
                    href={ cert.href }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={ cert.src }
                      alt={ cert.alt }
                      width={ cert.width } height={ cert.height }
                      style={{ height: cert.displayHeight, width: 'auto' }}
                      loading="lazy" decoding="async"
                    />
                  </a>
                )) }
              </div>
            </div>

          </div>
        </div>
      </div>

      { /* Franja inferior */ }
      <div className="bs-footer__band">
        <div className="bs-footer__bottom-inner">
          <p className="bs-footer__bottom-text">
            <span>© { year } { RIGHTS }</span>
            <span aria-hidden="true" className="bs-footer__sep">·</span>
            <a href={ LEGAL_LINKS.privacy }>Privacidad</a>
            <span aria-hidden="true" className="bs-footer__sep">·</span>
            <a href={ LEGAL_LINKS.cookies }>Cookies</a>
            <span aria-hidden="true" className="bs-footer__sep">·</span>
            { /* Reabre el panel de preferencias. Lo escucha BasisCookieConsent
                 por delegacion en document: el pie es otro arbol de React y no
                 hay contexto entre los dos. */ }
            <button type="button" data-consent-reopen className="bs-footer__linkbtn">
              Preferencias de cookies
            </button>
            <span aria-hidden="true" className="bs-footer__sep">·</span>
            <span className="bs-footer__attrib">
              Explorador con{ ' ' }
              <a href="https://www.blockscout.com" target="_blank" rel="noopener noreferrer">Blockscout</a>
            </span>
            { apiVersionUrl && backendVersionData?.backend_version && (
              <>
                <span aria-hidden="true" className="bs-footer__sep">·</span>
                <a href={ apiVersionUrl } target="_blank" rel="noopener noreferrer">
                  backend { backendVersionData.backend_version }
                </a>
              </>
            ) }
            { frontendVersion && (
              <>
                <span aria-hidden="true" className="bs-footer__sep">·</span>
                <a
                  href={ `https://github.com/blockscout/frontend/tree/${ frontendVersion }` }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  frontend { frontendVersion }
                </a>
              </>
            ) }
          </p>
        </div>
      </div>
    </footer>
  );
};

export default React.memo(BasisFooter);
